import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Form } from "@av/components/atoms/form/Form";
import { Input } from "@av/components/atoms/input/Input";
import { Modal } from "@av/components/atoms/Modal";
import { Typography } from "@av/components/atoms/typography/Typography";
import { Switch } from "@av/components/molecules/Switch";
import {
  getCompanyDocumentValidationError,
  setNameValidationError,
} from "../company-document-form";
import { MODAL_WIDTH } from "../constants";
import { useCompanyKnowledgeStyles } from "../styles";
import { ModalActions } from "./ModalActions";

import type { CompanyKnowledgeDocument, DocumentFormValues } from "../types";

type EditDocumentModalProps = {
  document?: CompanyKnowledgeDocument;
  isSaving?: boolean;
  onCancel: () => void;
  onSave: (values: Pick<DocumentFormValues, "name" | "description" | "useInAva">) => Promise<void>;
};

export function EditDocumentModal({
  document,
  isSaving,
  onCancel,
  onSave,
}: EditDocumentModalProps) {
  const { t } = useTranslation();
  const { styles } = useCompanyKnowledgeStyles();
  const [form] = Form.useForm<DocumentFormValues>();
  const [saveError, setSaveError] = useState<string | undefined>();

  const handleCancel = () => {
    setSaveError(undefined);
    onCancel();
  };

  return (
    <Modal
      destroyOnHidden
      footer={null}
      okText={t("platform.adminSettings.companyKnowledge_save")}
      onCancel={handleCancel}
      open={!!document}
      testIdPrefix="company-knowledge-edit-document"
      title={t("platform.adminSettings.companyKnowledge_editDocument")}
      width={MODAL_WIDTH}
    >
      <Form
        form={form}
        initialValues={{
          name: document?.name,
          description: document?.description,
          useInAva: document?.useInAva,
        }}
        onFinish={async (values) => {
          setSaveError(undefined);

          try {
            await onSave(values);
          } catch (error) {
            const nameValidationError = getCompanyDocumentValidationError(error, "name");

            setNameValidationError(form, error);
            setSaveError(
              nameValidationError
                ? undefined
                : t("platform.adminSettings.companyKnowledge_saveFailed")
            );
            // Keep the modal open so the user can retry.
          }
        }}
      >
        <Form.Item
          label={t("platform.adminSettings.companyKnowledge_name")}
          name="name"
          rules={[
            {
              required: true,
              message: t("platform.adminSettings.companyKnowledge_nameRequired"),
            },
            {
              whitespace: true,
              message: t("platform.adminSettings.companyKnowledge_nameRequired"),
            },
          ]}
          testIdPrefix="company-knowledge-edit-document-name"
        >
          <Input
            placeholder={t("platform.adminSettings.companyKnowledge_namePlaceholder")}
            testIdPrefix="company-knowledge-edit-document-name"
          />
        </Form.Item>
        <Form.Item
          label={t("platform.adminSettings.companyKnowledge_description")}
          name="description"
          testIdPrefix="company-knowledge-edit-document-description"
        >
          <Input.TextArea
            placeholder={t("platform.adminSettings.companyKnowledge_descriptionPlaceholder")}
            testIdPrefix="company-knowledge-edit-document-description"
          />
        </Form.Item>
        <Form.Item
          className={styles.switchFormItem}
          label={t("platform.adminSettings.companyKnowledge_avaAccess")}
          name="useInAva"
          testIdPrefix="company-knowledge-edit-document-ava-access"
          valuePropName="checked"
        >
          <Switch testIdPrefix="company-knowledge-edit-document-ava-access" />
        </Form.Item>
        {saveError && (
          <Typography.Text
            role="alert"
            testIdPrefix="company-knowledge-edit-document-save-error"
            type="danger"
          >
            {saveError}
          </Typography.Text>
        )}
        <ModalActions
          cancelTestIdPrefix="company-knowledge-edit-document-cancel"
          isSaving={isSaving}
          okText={t("platform.adminSettings.companyKnowledge_save")}
          okTestIdPrefix="company-knowledge-edit-document-submit"
          onCancel={handleCancel}
        />
      </Form>
    </Modal>
  );
}
